import { cn } from '@/lib/utils/cn'

export interface PageHeaderBandProps {
  title: React.ReactNode
  /**
   * Context copy under the title. Hidden below the shell's `lg` (1024px)
   * mobile breakpoint — it's explanatory text, not data an operator needs,
   * and the Foundation Docs' page-header contract already excludes
   * subtitles from this band. See `01-layout-system.md`.
   */
  description?: React.ReactNode
  /**
   * Everything after the title block — `ScopeChip`, `MetricStrip`,
   * action buttons, in whatever combination the page needs. Rendered
   * as-is; this component only owns the row's responsive wrapping.
   */
  children?: React.ReactNode
  className?: string
}

/**
 * Shared page-header band (title + optional description + KPIs/actions).
 * Previously hand-copied identically across 17 pages — one copy wrapped a
 * long two-word KPI label (e.g. "Awaiting Ship"), which grew that card
 * and, since the row is `items-center`, the entire header's height along
 * with it. `flex-wrap` here means that if title + children genuinely
 * don't fit on one line at any viewport width, the children cluster
 * drops to its own row instead of an individual element wrapping/growing.
 * At real desktop widths every existing caller already fits on one line,
 * so `flex-wrap` is a no-op there.
 */
export function PageHeaderBand({
  title,
  description,
  children,
  className,
}: PageHeaderBandProps) {
  return (
    <header
      className={cn(
        'flex flex-shrink-0 flex-wrap items-center gap-6 border-b border-border-subtle bg-surface px-6 py-3',
        className,
      )}
    >
      <div className="flex min-w-0 flex-col gap-0.5">
        <h1 className="text-role-page-title leading-tight tracking-tight">{title}</h1>
        {description ? (
          <p className="text-role-page-subtitle hidden lg:block">{description}</p>
        ) : null}
      </div>
      {children}
    </header>
  )
}
